import { SyncedDocStore } from '@/doc-sync/synced-doc-store';

import type { DiagramMetaExtra, DiagramStore, SyncRevisionStore } from './diagram-store';
import { diagramMetaExtraFromRemote, type RemoteDiagramStore, type SeedResult } from './remote-diagram-store';
import type { StoredDiagram } from './stored-diagram';

export type RemoteClient = Pick<RemoteDiagramStore, 'list' | 'get' | 'put' | 'delete' | 'putThumbnail' | 'getThumbnail'>
    & Partial<Pick<RemoteDiagramStore, 'seed' | 'projection' | 'archive' | 'unarchive'>>;

/** Write-through DiagramStore: local IndexedDB is always the fast path and
 *  the source of truth when offline, remote is synced alongside it. Reads
 *  prefer remote (freshest across devices) but fall back to local whenever
 *  the remote is unreachable; writes land locally first so a flaky network
 *  never loses data. `list()` also drives two best-effort background
 *  passes — uploading local-only (phase-1) docs and backfilling thumbnails
 *  the local store hasn't cached yet — neither of which blocks the call
 *  that triggered them.
 *
 *  An optional `prepare` hook runs once, at construction, and every method
 *  below awaits it before doing anything else — this is how a caller (e.g.
 *  the account-switch ownership check in store-instance.ts) gets to run
 *  async reconciliation (potentially wiping `local`) before any op is
 *  allowed to read or write. */
export class SyncedDiagramStore extends SyncedDocStore<StoredDiagram, DiagramMetaExtra> {
    private readonly diagramRemote: RemoteClient;

    constructor(opts: { local: DiagramStore & SyncRevisionStore; remote: RemoteClient; prepare?: () => Promise<void>; now?: () => number }) {
        super({ ...opts, metaExtraFromRemote: diagramMetaExtraFromRemote });
        this.diagramRemote = opts.remote;
    }

    /** Converts this diagram to Yjs on the server (SP-52). */
    async seedYDoc(id: string, revision: number, update: Uint8Array): Promise<SeedResult> {
        if (this.diagramRemote.seed == null) throw new Error('remote does not support seeding');
        return this.diagramRemote.seed(id, revision, update);
    }

    /** Offers the JSON projection of a Yjs-synced diagram to the server. */
    async offerProjection(doc: StoredDiagram, stateVector: Uint8Array): Promise<{ accepted: boolean }> {
        if (this.diagramRemote.projection == null) throw new Error('remote does not support projections');
        return this.diagramRemote.projection(doc, stateVector);
    }

    /** Freezes this diagram on the server. The device copy (IndexedDB and Yjs
     *  state) is left exactly as it is. Every gallery listing this store is told
     *  to re-list (subscribeBackgroundSync), whoever asked for the archive. */
    async archive(id: string): Promise<void> {
        if (this.diagramRemote.archive == null) throw new Error('remote does not support archiving');
        await this.diagramRemote.archive(id);
        this.notifyBackgroundSync();
    }

    /** Takes this diagram out of the archive, or reports the plan limit that
     *  leaves it no free active slot (then nothing changed, and no one is told). */
    async unarchive(id: string): Promise<'unarchived' | { limit: number }> {
        if (this.diagramRemote.unarchive == null) throw new Error('remote does not support archiving');
        const result = await this.diagramRemote.unarchive(id);
        if (result === 'unarchived') this.notifyBackgroundSync();
        return result;
    }
}
